import { zodResolver } from '@hookform/resolvers/zod';
import { CUSTOMER_LEVEL } from '@wyz/types';
import {
  Button,
  Card,
  CenteredSpinner,
  Col,
  Flex,
  FormContainer,
  Icon,
  Row,
  Title,
} from '@wyz/ui';
import { SUPPLIERS_TYPES_IDS, useOnSuccessQuery } from '@wyz/utils';
import * as React from 'react';
import { useFieldArray, useForm, useWatch } from 'react-hook-form';
import {
  CriteriaBuilder,
  MatchedCustomersTable,
  TextField,
  SelectField,
} from '~/common/components';
import {
  SupplierDraggables,
  SupplierDraggableItem,
  RedistributionBusinessRulesCard,
} from '~/connect/components';
import { Trans } from '~/common/context';
import { FEATURE_CONTEXT } from '~/common/schemas';
import {
  redistributionSupplierTemplateFormSchema,
  REDISTRIBUTION_BUSINESS_RULES_DEFAULTS,
  RedistributionRuleStatus,
  SUPPLIERS_STATUSES,
} from '~/connect/schemas';
import {
  IRedistributionBusinessRulesForm,
  IRedistributionSupplierTemplateForm,
} from '~/connect/types';
import { useMyCustomers } from '~/common/hooks';
import { useManageCustomers } from '~/common/api';
import {
  applyBusinessRuleToSuppliers,
  applyBusinessRulesToSuppliers,
  deriveBusinessRulesFromSuppliers,
  tryReorderSuppliers,
} from '~/connect/helpers';

// eslint-disable-next-line @wyz/module/no-relative-import
import './form.scss';
import { Link } from 'react-router';

type Props = {
  defaultValues?: IRedistributionSupplierTemplateForm;
  onSubmit: (data: IRedistributionSupplierTemplateForm) => void;
  isSubmitting: boolean;
};

const RedistributionSupplierTemplateFormContainer = ({
  defaultValues,
  onSubmit,
  isSubmitting,
}: Props) => {
  const getCustomersByUser = useMyCustomers({
    minLevel: CUSTOMER_LEVEL.ROOT,
  });

  const methods = useForm<IRedistributionSupplierTemplateForm>({
    resolver: zodResolver(redistributionSupplierTemplateFormSchema),
    defaultValues: defaultValues || {
      name: '',
      criteria: [],
      suppliers: [],
      businessRules: { ...REDISTRIBUTION_BUSINESS_RULES_DEFAULTS },
    },
  });
  const watchedCustomerScope = useWatch({
    name: 'customerScope',
    control: methods.control,
  });
  const customer = watchedCustomerScope
    ? getCustomersByUser.data?.data.find(
        (item) => item.id === watchedCustomerScope,
      )
    : undefined;

  const manageCustomers = useManageCustomers({
    getSuppliersByCustomer: {
      enabled: Boolean(watchedCustomerScope),
      args: {
        customerId: watchedCustomerScope || '',
      },
    },
  });

  useFieldArray<IRedistributionSupplierTemplateForm, 'suppliers', 'key'>({
    control: methods.control,
    name: 'suppliers',
    keyName: 'key',
  });
  const suppliers = useWatch({
    name: 'suppliers',
    control: methods.control,
  });

  useOnSuccessQuery(manageCustomers.getSuppliersByCustomer, (data) => {
    const suppliers = data.data;

    if (
      defaultValues?.suppliers &&
      defaultValues.customerScope === watchedCustomerScope
    ) {
      methods.setValue(
        'suppliers',
        defaultValues.suppliers.map((supplier) => ({
          ...supplier,
          _internalStock:
            supplier.type === SUPPLIERS_TYPES_IDS.PLATFORM_DISTRIBUTOR,
        })),
      );
      return;
    }

    // Fresh suppliers start from the template's business rules, which on the create screen come
    // from the platform's generic rule.
    methods.setValue(
      'suppliers',
      applyBusinessRulesToSuppliers(
        suppliers.map((supplier) => ({
          ...supplier,
          customName: '',
          // ui-only fields
          enabled: supplier.status === SUPPLIERS_STATUSES.ENABLED,
          _internalStock:
            supplier.type === SUPPLIERS_TYPES_IDS.PLATFORM_DISTRIBUTOR,
        })),
        methods.getValues('businessRules'),
      ),
    );
  });

  const businessRules = useWatch({
    name: 'businessRules',
    control: methods.control,
  });

  // The supplier-backed statuses are a projection of the supplier list; only the DMS rules are
  // genuine form state.
  const displayedRules = deriveBusinessRulesFromSuppliers(
    suppliers,
    businessRules,
  );

  const handleRuleChange = React.useCallback(
    (
      field: keyof IRedistributionBusinessRulesForm,
      status: RedistributionRuleStatus,
    ) => {
      methods.setValue(
        'suppliers',
        applyBusinessRuleToSuppliers(
          methods.getValues('suppliers'),
          field,
          status,
        ),
      );
    },
    [methods],
  );

  const handleMove = React.useCallback(
    (from: number, to: number) => {
      const newSuppliers = tryReorderSuppliers(suppliers, from, to);
      if (newSuppliers) {
        methods.setValue('suppliers', newSuppliers);
      }
    },
    [suppliers, methods],
  );

  const [configuredSupplierIndex, setConfiguredSupplierIndex] = React.useState<
    number | undefined
  >();
  const configuredSupplier =
    configuredSupplierIndex !== undefined
      ? suppliers[configuredSupplierIndex]
      : undefined;
  return (
    <FormContainer
      methods={methods}
      submitHandler={(data) => onSubmit(data)}
      className='space-y-2 rst-form'
    >
      {manageCustomers.getSuppliersByCustomer.isProcessing && (
        <div
          className='position-absolute h-100 w-100'
          style={{ backgroundColor: 'rgba(0,0,0,.1)', zIndex: 5 }}
        >
          <div className='center-v-h'>
            <CenteredSpinner />
          </div>
        </div>
      )}
      <Card className='mb-section'>
        <Card.Header>
          <Title heading='lg' color='tertiary' weight='bold'>
            <Icon icon='post' width='1.5rem' height='1.5rem' />
            <span className='ms-2'>
              <Trans i18nKey='app.common.general_information' />
            </span>
          </Title>
        </Card.Header>
        <Card.Body>
          <Row>
            <TextField
              name='name'
              label={<Trans i18nKey='app.common.model_name' />}
            />
          </Row>
          <Col md={12}>
            <SelectField<false>
              name='customerScope'
              label={<Trans i18nKey='app.common.customer_scope.label.source' />}
              options={
                getCustomersByUser.data?.data
                  .filter((item) => item.level === CUSTOMER_LEVEL.N1)
                  .map((item) => ({
                    label: item.name,
                    value: item.id,
                  })) || []
              }
              size='sm'
              maxShown={Infinity}
              required
              isLoading={getCustomersByUser.isLoading}
            />
          </Col>
        </Card.Body>
      </Card>

      <Card className='mb-section'>
        <Card.Header className='pb-0'>
          <Title heading='lg' color='tertiary' weight='bold'>
            <Icon icon='clip-board' width='1.5rem' height='1.5rem' />
            <span className='ms-2'>
              <Trans i18nKey='app.common.criteria' />
            </span>
          </Title>
        </Card.Header>
        <Card.Body>
          <CriteriaBuilder name={'criteria'} disabled={!customer}>
            <Row className='mt-4'>
              <Col>
                {customer && (
                  <MatchedCustomersTable
                    featureContext={
                      FEATURE_CONTEXT.REDISTRIBUTION_SUPPLIER_TEMPLATE
                    }
                    name={'criteria'}
                    matchUserContext
                    queryOverrideParams={{
                      parentIds: [customer.id],
                    }}
                  />
                )}
              </Col>
            </Row>
          </CriteriaBuilder>
        </Card.Body>
      </Card>
      <RedistributionBusinessRulesCard
        statuses={displayedRules}
        onRuleChange={handleRuleChange}
      />
      <Card className='mb-section'>
        <Card.Header className='pb-0'>
          <Title heading='lg' color='tertiary' weight='bold'>
            <Icon icon='team' width='1.5rem' height='1.5rem' />
            <span className='ms-2'>
              <Trans i18nKey='app.supplier_template.form.suppliers.title' />
            </span>
          </Title>
        </Card.Header>
        <Card.Body>
          <Row>
            <Col md={configuredSupplier ? 7 : 12} className='animate-slide'>
              <Title heading='lg' weight='bold'>
                <Trans
                  i18nKey='app.supplier_template.form.suppliers.order_title'
                  values={{ count: suppliers.length }}
                />
              </Title>
              <SupplierDraggables
                featureContext={
                  FEATURE_CONTEXT.REDISTRIBUTION_SUPPLIER_TEMPLATE
                }
                onMove={handleMove}
                suppliers={suppliers}
                onConfigure={(supplier) =>
                  setConfiguredSupplierIndex(
                    suppliers.findIndex(
                      (s) => s.wyzApiId === supplier.wyzApiId,
                    ),
                  )
                }
                onRemove={(id) => {
                  const newSuppliers = suppliers.filter(
                    (s) => s.wyzApiId !== id,
                  );
                  methods.setValue('suppliers', newSuppliers);
                }}
                onConfigureBestSourcing={() => {}}
                canDrag={(supplier) => supplier._internalStock === true}
                canRemove={(supplier) => supplier._internalStock === true}
              />
            </Col>
            {configuredSupplier && configuredSupplierIndex !== undefined && (
              <Col
                key={configuredSupplierIndex}
                md={5}
                className='animate-slide'
                style={{ borderLeft: '1px solid var(--neutral-grey-4)' }}
              >
                <Title heading='lg' weight='bold' wrap>
                  <Trans
                    i18nKey='app.supplier_template.form.suppliers.configure_supplier'
                    values={{ supplierName: configuredSupplier.name }}
                  />
                </Title>
                <SupplierDraggableItem
                  baseName={`suppliers.${configuredSupplierIndex}`}
                  supplier={{
                    id: configuredSupplier.wyzApiId,
                    type: configuredSupplier.type,
                  }}
                  index={configuredSupplierIndex}
                  display={{
                    orderable: false,
                    childCustomerDelivery: true,
                    autoValidation: true,
                    rankingModel: false,
                    extendedManufConfiguration: false,
                    overridePriorityIfShorterDelivery: false,
                  }}
                  disabled={{
                    childCustomerDelivery: configuredSupplier._internalStock,
                    enabled: configuredSupplier._internalStock ? false : true,
                  }}
                />
              </Col>
            )}
          </Row>
        </Card.Body>
      </Card>
      <Card>
        <Card.Body>
          <Flex alignItems='center' justifyContent='between'>
            <Button
              id='cancel'
              type='button'
              variant='secondary'
              disabled={isSubmitting}
              as={Link}
              to='..'
            >
              <Trans i18nKey='app.common.button.cancel' />
            </Button>
            <Button
              id='create-rst'
              type='submit'
              variant='primary'
              isLoading={isSubmitting}
              disabled={getCustomersByUser.isLoading}
            >
              <Trans i18nKey='app.common.button.save' />
            </Button>
          </Flex>
        </Card.Body>
      </Card>
    </FormContainer>
  );
};

export default RedistributionSupplierTemplateFormContainer;
